<script setup>
import { ref, onMounted, computed, watch } from 'vue'; // Import watch
import axios from 'axios';
import { nextTick } from 'vue'; // Import nextTick for notification
import { API_BASE_URL } from '@/config';

// Reactive state for the list of employees
const employees = ref([]);
// Reactive state for the lists to populate dropdowns
const sites = ref([]);
const departments = ref([]);

// Reactive state for the new employee form input
const newEmployee = ref({
    emp_no: '',
    emp_name: '',
    designation: '', // Will store Employee Type (employee, requester, retired)
    // emp_cat_no and emp_cat_name are removed from the form and payload
    office_phone: '', // Removed from form, but kept in state for backend (sent as null if not in form)
    house_phone: '', // Removed from form, but kept in state for backend (sent as null if not in form)
    mobile: '',
    site_no: '', // Will hold selected site number (used for form dropdowns)
    dep_no: '', // Will hold selected department number
    department: '', // Will hold selected department name (derived from dep_no selection)
    address: '',
    h_salary: null, // Use null for numeric fields that can be empty
    ot1: null,
    ot2: null,
    ot3: null
});
// Reactive state to hold the employee currently being edited (null if not editing)
const editingEmployee = ref(null);

// Reactive state to track the row clicked in the table for Edit/Delete actions
const selectedEmployee = ref(null);

// Reactive state to hold the currently selected site for department filtering
const selectedSiteNo = ref('');

// Loading state specifically for the employee table data
const tableLoading = ref(false);


// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
const API_EMP_URL = '${API_BASE_URL}/api/employees';
const API_SITES_URL = '${API_BASE_URL}/api/sites'; // API endpoint for sites
const API_DEPARTMENTS_URL = '${API_BASE_URL}/api/departments'; // API endpoint for departments


const loading = ref(false); // Loading state for async operations (e.g., saving, deleting)
const error = ref(null); // Error state for displaying persistent error messages
// State for managing temporary notification messages (success, error, warning)
const notification = ref({
    show: false,
    message: '',
    type: '' // 'success', 'error', 'warning'
});

// Options for the Employee Type dropdown
const employeeTypes = ['employee', 'requester', 'retired'];


// --- Computed Properties for v-model Binding ---
// This computed property handles the binding for the form inputs.
// It switches between the 'editingEmployee' and 'newEmployee' objects based on whether editing is active.
const currentEmployee = computed({
    get() {
        return editingEmployee.value || newEmployee.value;
    },
    set(value) {
        if (editingEmployee.value) {
            Object.assign(editingEmployee.value, value);
        } else {
            Object.assign(newEmployee.value, value);
        }
    }
});

// Computed property to easily get site name from site_no for table display
const siteNameMap = computed(() => {
    const map = {};
    sites.value.forEach(site => {
        map[site.site_no] = site.site_name;
    });
    return map;
});

// Computed property to get the site name for an employee based on their department's site_no
const employeeSiteName = computed(() => (employee) => {
    if (!employee || !employee.dep_no) {
        console.log(`employeeSiteName: Employee or dep_no missing for employee:`, employee);
        return 'N/A'; // Return N/A if employee or department number is missing
    }
    console.log(`employeeSiteName: Looking up department for dep_no: ${employee.dep_no}`);
    // Find the department object that matches the employee's department number
    const department = departments.value.find(dep => dep.dep_no === employee.dep_no);

    if (department && department.Site_no) { // Checks if department and its Site_no exist
        console.log(`employeeSiteName: Found department ${department.dep_desc} with Site_no: ${department.Site_no}`);
        // Use the siteNameMap to find the site name using the department's Site_no
        const siteName = siteNameMap.value[department.Site_no];
        console.log(`employeeSiteName: Found site name: ${siteName}`);
        return siteName || `Site ${department.Site_no}`; // Fallback to Site Code if name not found
    }

    console.log(`employeeSiteName: Department not found for dep_no ${employee.dep_no} or missing Site_no. Found department:`, department);
    return 'N/A'; // Return N/A if department or its site_no is not found
});


// --- Data Fetching for Dropdowns ---

// Fetches all sites
const fetchSites = async () => {
    try {
        const response = await axios.get(API_SITES_URL);
        // Assuming your site objects have a 'SiteCode' and 'Sitename' property
        sites.value = response.data.map(site => ({ site_no: site.SiteCode, site_name: site.Sitename }));
        console.log('Fetched sites:', sites.value);
    } catch (err) {
        console.error('Error fetching sites:', err);
        // Optionally show a notification or error message for sites
    }
};

// Fetches departments, optionally filtered by siteNo
const fetchDepartments = async (siteNo = '') => {
     departments.value = []; // Clear current departments
     // If a site is selected, fetch departments for that site
     if (siteNo) {
         try {
             // Use the new backend endpoint to get departments by site
             // IMPORTANT: Ensure this URL matches your backend route!
             const response = await axios.get(`${API_DEPARTMENTS_URL}/bysite/${siteNo}`);
             // Assuming your department objects have 'dep_no', 'dep_desc', and 'Site_no'
             departments.value = response.data.map(dep => ({ dep_no: dep.dep_no, department: dep.dep_desc, Site_no: dep.Site_no }));
             console.log(`Fetched departments for site ${siteNo}:`, departments.value);
         } catch (err) {
             console.error(`Error fetching departments for site ${siteNo}:`, err);
             // Optionally show a notification or error message
         }
     } else {
         // If no site is selected, fetch all departments
         try {
             const response = await axios.get(API_DEPARTMENTS_URL); // Assuming this endpoint returns all departments
              departments.value = response.data.map(dep => ({ dep_no: dep.dep_no, department: dep.dep_desc, Site_no: dep.Site_no }));
             console.log('Fetched all departments:', departments.value);
         } catch (err) {
              console.error('Error fetching all departments:', err);
         }
     }
};


// --- Watchers ---
// Watch for changes in the selected site and fetch departments accordingly
watch(selectedSiteNo, (newSiteNo) => {
    // Update the site_no in the current employee object (for form data)
    currentEmployee.value.site_no = newSiteNo;

    // Reset the department selection when the site changes
    if (editingEmployee.value) {
        editingEmployee.value.dep_no = '';
        editingEmployee.value.department = '';
    } else {
        newEmployee.value.dep_no = '';
        newEmployee.value.department = '';
    }
    fetchDepartments(newSiteNo);
});


// --- CRUD Operations ---

// Fetches all employees from the backend API
const fetchEmployees = async () => {
    tableLoading.value = true; // Set table loading state to true
    error.value = null; // Clear any previous errors
    selectedEmployee.value = null; // Deselect any selected item when refreshing the list
    try {
        // Send GET request to the API endpoint
        const response = await axios.get(API_EMP_URL);
        // Update the employees ref with the data received from the backend
        employees.value = response.data;
        console.log('Fetched employees:', employees.value);
    } catch (err) {
        console.error('Error fetching employees:', err);
        error.value = 'Failed to fetch employees. Please try again later.';
        showNotification(error.value, 'error'); // Show an error notification
    } finally {
        tableLoading.value = false; // Set table loading state to false regardless of success or failure
    }
};

// Creates a new employee by sending data to the backend API
const createEmployee = async () => {
    // Basic client-side validation: check if required fields are not empty
    // Required fields are now emp_no, emp_name, selected dep_no, and selected designation (Employee Type)
    if (!newEmployee.value.emp_no.trim() || !newEmployee.value.emp_name.trim() || !newEmployee.value.dep_no || !newEmployee.value.designation) {
        showNotification('Employee Number, Name, Department, and Employee Type are required.', 'warning');
        return; // Stop the function if validation fails
    }

    // Find the selected department name based on dep_no
    const selectedDepartment = departments.value.find(dep => dep.dep_no === newEmployee.value.dep_no);
    if (selectedDepartment) {
        newEmployee.value.department = selectedDepartment.department; // Set the department name
        newEmployee.value.site_no = selectedDepartment.Site_no; // Also set the site_no from the department for the payload
    } else {
        newEmployee.value.department = ''; // Should not happen if dep_no is required and selected from list
        newEmployee.value.site_no = ''; // Clear site_no if department is invalid
        showNotification('Invalid Department selected.', 'warning');
        return;
    }

    // Construct the payload with fields to be sent to the backend
    const payload = {
        emp_no: newEmployee.value.emp_no,
        emp_name: newEmployee.value.emp_name,
        designation: newEmployee.value.designation, // Employee Type
        mobile: newEmployee.value.mobile || null, // Allow null
        dep_no: newEmployee.value.dep_no, // Send the selected department number
        department: newEmployee.value.department, // Send the department name
        site_no: newEmployee.value.site_no || null, // Send the site_no derived from the department
        // Explicitly setting fields removed from the form to null
        emp_cat_no: null,
        emp_cat_name: null,
        office_phone: null,
        house_phone: null,
        // Include other fields if they are in the form and need to be saved,
        // ensuring empty strings are sent as null if the database expects null.
        address: newEmployee.value.address || null,
        h_salary: newEmployee.value.h_salary || null,
        ot1: newEmployee.value.ot1 || null,
        ot2: newEmployee.value.ot2 || null,
        ot3: newEmployee.value.ot3 || null,
    };


    loading.value = true; // Set loading state
    error.value = null; // Clear previous errors
    try {
        // Send POST request to the API endpoint with the new employee data
        const response = await axios.post(API_EMP_URL, payload); // Use the cleaned payload

        // Add the newly created item (returned from the backend) to the local list
        // This should trigger Vue to update the table display IF response.data is valid
        employees.value.push(response.data);
        // Clear the form inputs
        resetNewEmployeeForm(); // Use a helper function to reset
        showNotification('Employee created successfully!', 'success'); // Show success notification
    } catch (err) {
        console.error('Error creating employee:', err);
        // Handle specific error responses from the backend
        if (err.response?.status === 409) {
            error.value = 'Employee Number already exists.';
        } else {
            // This will catch potential 500 errors from the backend
            error.value = err.response?.data || 'Failed to create employee.';
        }
        showNotification(error.value, 'error'); // Show error notification
    } finally {
        loading.value = false; // Set loading state to false
    }
};

// Updates an existing employee by sending data to the backend API
const updateEmployee = async () => {
    // Ensure we are in editing mode and an employee is selected
    if (!editingEmployee.value || !selectedEmployee.value) {
        showNotification('No employee selected for editing.', 'warning');
        return;
    }
    // Basic client-side validation for updateable fields
    // Required fields are now emp_name, selected dep_no, and selected designation
    if (!editingEmployee.value.emp_name.trim() || !editingEmployee.value.dep_no || !editingEmployee.value.designation) {
        showNotification('Employee Name, Department, and Employee Type are required.', 'warning');
        return;
    }

    // Find the selected department name based on dep_no for the updated data
    const selectedDepartment = departments.value.find(dep => dep.dep_no === editingEmployee.value.dep_no);
    if (selectedDepartment) {
        editingEmployee.value.department = selectedDepartment.department; // Set the department name
        editingEmployee.value.site_no = selectedDepartment.Site_no; // Also set the site_no from the department for the payload
    } else {
         editingEmployee.value.department = ''; // Should not happen if dep_no is required and selected from list
         editingEmployee.value.site_no = ''; // Clear site_no if department is invalid
         showNotification('Invalid Department selected.', 'warning');
         return;
    }

    // Construct the payload with fields to be sent to the backend
    const payload = {
        emp_name: editingEmployee.value.emp_name,
        designation: editingEmployee.value.designation, // Employee Type
        mobile: editingEmployee.value.mobile || null, // Allow null
        dep_no: editingEmployee.value.dep_no, // Send the selected department number
        department: editingEmployee.value.department, // Send the department name
        site_no: editingEmployee.value.site_no || null, // Send the site_no derived from the department
         // Explicitly setting fields removed from the form to null
        emp_cat_no: null,
        emp_cat_name: null,
        office_phone: null,
        house_phone: null,
        // Include other fields if they are in the form and need to be saved,
        // ensuring empty strings are sent as null if the database expects null.
        address: editingEmployee.value.address || null,
        h_salary: editingEmployee.value.h_salary || null,
        ot1: editingEmployee.value.ot1 || null,
        ot2: editingEmployee.value.ot2 || null,
        ot3: editingEmployee.value.ot3 || null,
    };


    loading.value = true; // Set loading state
    error.value = null; // Clear previous errors
    try {
        // Send PUT request to the specific item endpoint using the emp_no from the selected item
        // Ensure the URL is correct, using the emp_no from the selected employee
        const response = await axios.put(`${API_EMP_URL}/${selectedEmployee.value.emp_no}`, payload);

        // Find the index of the updated item in the local list using its emp_no
        const index = employees.value.findIndex(item => item.emp_no === selectedEmployee.value.emp_no);
        if (index !== -1) {
            // Update the item in the local list with the data returned from the backend
            employees.value[index] = { ...employees.value[index], ...response.data };
        }
         // If the updated item was the one currently selected, update the selected item ref as well
         if (selectedEmployee.value && selectedEmployee.value.emp_no === editingEmployee.value.emp_no) {
             selectedEmployee.value = { ...selectedEmployee.value, ...response.data };
         }

        editingEmployee.value = null; // Exit editing mode
        showNotification('Employee updated successfully!', 'success'); // Show success notification
    } catch (err) {
        console.error('Error updating employee:', err);
        // Handle specific error responses from the backend
        if (err.response?.status === 404) {
            error.value = 'Employee not found.';
        } else if (err.response?.status === 400) {
                 error.value = err.response.data || 'Bad Request: Could not update employee.';
             } else if (err.response?.status === 500) {
                 error.value = 'Server error during update.';
             }
        else {
            error.value = 'Failed to update employee.';
        }
        showNotification(error.value, 'error'); // Show error notification
    } finally {
        loading.value = false; // Set loading state to false
    }
};

// Deletes an employee by their unique emp_no
const deleteEmployee = async () => {
    // Ensure an employee is selected for deletion
    if (!selectedEmployee.value) {
        showNotification('Please select an employee to delete.', 'warning');
        return;
    }
    // Get the emp_no of the item to delete from the selected item
    const empNoToDelete = selectedEmployee.value.emp_no;

    if (confirm(`Are you sure you want to delete employee ${empNoToDelete}? This may also delete associated records.`)) {
        loading.value = true;
        error.value = null;
        try {
            // Send DELETE request to the specific item endpoint using the emp_no
            await axios.delete(`${API_EMP_URL}/${empNoToDelete}`);
            // Remove the deleted item from the local list by filtering
            employees.value = employees.value.filter(item => item.emp_no !== empNoToDelete);
            // Clear selected and editing states if they match the deleted item
            selectedEmployee.value = null;
            if(editingEmployee.value && editingEmployee.value.emp_no === empNoToDelete) {
                 editingEmployee.value = null;
            }
            showNotification('Employee deleted successfully!', 'success');
        } catch (err) {
            console.error('Error deleting employee:', err);
            // Handle specific error responses from the backend
            if (err.response?.status === 404) {
                error.value = 'Employee not found.';
            } else if (err.response?.status === 409) {
                 error.value = err.response.data || 'Conflict: Cannot delete employee because it is referenced by other entities.';
             }
            else {
                error.value = 'Failed to delete employee.';
            }
            showNotification(error.value, 'error');
        } finally {
            loading.value = false;
        }
    }
};

// --- UI Actions ---

// Selects a row in the table when clicked
const selectEmployee = (item) => {
    selectedEmployee.value = item;
    // When selecting an employee, update the selected site and fetch departments for that site
    // We need to find the site_no based on the employee's dep_no
    const department = departments.value.find(dep => dep.dep_no === item.dep_no);
    selectedSiteNo.value = department ? department.Site_no : '';

    // If you select an item while currently editing another, cancel the editing
    if (editingEmployee.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors when selecting a new row
}

// Initiates the editing process for the currently selected employee
const startEditing = () => {
    if (selectedEmployee.value) {
         // Copy selected item data to the editing state to populate the form
         editingEmployee.value = { ...selectedEmployee.value };
         // Set the selected site for the dropdown when starting edit
         // We need to find the site_no based on the employee's dep_no
         const department = departments.value.find(dep => dep.dep_no === selectedEmployee.value.dep_no);
         selectedSiteNo.value = department ? department.Site_no : '';

         resetNewEmployeeForm(); // Clear the new employee form when starting edit mode
         error.value = null; // Clear any previous errors
    } else {
         showNotification('Please select an employee to edit.', 'warning'); // Prompt user if no item is selected
    }
};

// Cancels the current editing process and clears the editing state
const cancelEditing = () => {
    editingEmployee.value = null; // Clear the editing state
    resetNewEmployeeForm(); // Clear the new employee form (as it shares inputs via computed properties)
     selectedSiteNo.value = ''; // Reset selected site
     departments.value = []; // Clear departments list
    error.value = null; // Clear any previous errors
};

// Resets the form to the state for adding a new employee
 const addNew = () => {
      editingEmployee.value = null; // Ensure not in editing mode
      selectedEmployee.value = null; // Deselect any row in the table
      resetNewEmployeeForm(); // Clear form for new entry
      selectedSiteNo.value = ''; // Reset selected site
      departments.value = []; // Clear departments list
      error.value = null; // Clear any previous errors
 };

 // Helper to reset the new employee form
 // Includes resetting dropdown selections and removed fields
 const resetNewEmployeeForm = () => {
      newEmployee.value = {
        emp_no: '',
        emp_name: '',
        designation: '', // Reset Employee Type
        // emp_cat_no and emp_cat_name are removed from the form
        office_phone: '', // Reset phone fields (even if not in form)
        house_phone: '',
        mobile: '',
        site_no: '', // Reset selected site
        dep_no: '', // Reset selected department
        department: '',
        address: '',
        h_salary: null,
        ot1: null,
        ot2: null,
        ot3: null
      };
 };


// Helper function to show a temporary notification message
const showNotification = (message, type) => {
    notification.value.message = message;
    notification.value.type = type;
    notification.value.show = true;

    nextTick(() => {
         const notificationElement = document.querySelector('.notification');
         if (notificationElement) {
              notificationElement.classList.remove('fade-out');
              void notificationElement.offsetWidth; // Trigger reflow to restart animation
             setTimeout(() => {
                 notificationElement.classList.add('fade-out');
                 notificationElement.addEventListener('transitionend', () => {
                      if (!notificationElement.classList.contains('fade-out')) return; // Ensure it's the fade-out transition
                      notification.value.show = false;
                 }, { once: true }); // Remove listener after it runs once
             }, 5000); // Notification visible for 5 seconds
         } else {
              // Fallback if element not found immediately
              setTimeout(() => {
                  notification.value.show = false;
              }, 5500); // Allow a bit more time
         }
    });
};

// --- Lifecycle Hook ---
// Fetch sites and departments first, then employees
onMounted(async () => {
    await fetchSites(); // Wait for sites to load
    await fetchDepartments(); // Wait for all departments to load
    fetchEmployees(); // Then fetch employees
});
</script>

<template>
  <div class="employees-page">
    <div class="container">
      <div class="form-panel">
        <h2>{{ editingEmployee ? 'Edit Employee' : 'Add New Employee' }}</h2>
        <form @submit.prevent="editingEmployee ? updateEmployee() : createEmployee()">
          <div class="form-group">
            <label for="emp_no">Employee Number:</label>
              <input type="text" id="emp_no" v-model="currentEmployee.emp_no" required
                     :disabled="editingEmployee !== null || loading">
           </div>
          <div class="form-group">
            <label for="emp_name">Employee Name:</label>
              <input type="text" id="emp_name" v-model="currentEmployee.emp_name" required
                     :disabled="loading">
           </div>

           <div class="form-group">
                 <label for="designation">Employee Type:</label>
                 <select id="designation" v-model="currentEmployee.designation" required :disabled="loading">
                      <option value="">-- Select Type --</option>
                     <option v-for="type in employeeTypes" :key="type" :value="type">
                         {{ type.charAt(0).toUpperCase() + type.slice(1) }} </option>
                 </select>
            </div>

           <div class="form-group">
                 <label for="site_no">Site:</label>
                 <select id="site_no" v-model="selectedSiteNo" required :disabled="loading">
                     <option value="">-- Select Site --</option>
                     <option v-for="site in sites" :key="site.site_no" :value="site.site_no">
                         {{ site.site_name }} ({{ site.site_no }})
                     </option>
                 </select>
            </div>

           <div class="form-group">
                 <label for="dep_no">Department:</label>
                 <select id="dep_no" v-model="currentEmployee.dep_no" required :disabled="loading || !selectedSiteNo">
                     <option value="">-- Select Department --</option>
                     <option v-for="dep in departments" :key="dep.dep_no" :value="dep.dep_no">
                         {{ dep.department }} ({{ dep.dep_no }})
                     </option>
                 </select>
                   <small v-if="!selectedSiteNo" style="color: #666;">Select a Site to see departments.</small>
            </div>

            <div class="form-group">
                 <label for="mobile">Mobile Number:</label>
                 <input type="text" id="mobile" v-model="currentEmployee.mobile"
                        :disabled="loading">
            </div>

             <div class="form-group">
                 <label for="address">Address:</label>
                 <input type="text" id="address" v-model="currentEmployee.address"
                        :disabled="loading">
            </div>

             <div class="form-group">
                 <label for="h_salary">Hourly Salary:</label>
                 <input type="number" id="h_salary" v-model.number="currentEmployee.h_salary" step="0.01"
                        :disabled="loading">
            </div>
             <div class="form-group">
                 <label for="ot1">OT1 Rate:</label>
                 <input type="number" id="ot1" v-model.number="currentEmployee.ot1" step="0.01"
                        :disabled="loading">
            </div>
             <div class="form-group">
                 <label for="ot2">OT2 Rate:</label>
                 <input type="number" id="ot2" v-model.number="currentEmployee.ot2" step="0.01"
                        :disabled="loading">
            </div>
             <div class="form-group">
                 <label for="ot3">OT3 Rate:</label>
                 <input type="number" id="ot3" v-model.number="currentEmployee.ot3" step="0.01"
                        :disabled="loading">
            </div>


          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingEmployee ? 'Update Employee' : 'Save Employee' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingEmployee" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Employee List</h2>

          <div v-if="tableLoading">Loading employees...</div>
          <div v-if="error && employees.length === 0 && !tableLoading" class="error-message">{{ error }}</div>

        <div class="table-actions">
            <button @click="addNew" :disabled="loading || tableLoading">Add New</button>
            <button @click="startEditing" :disabled="!selectedEmployee || loading || tableLoading">Edit</button>
            <button @click="deleteEmployee" :disabled="!selectedEmployee || loading || tableLoading">Delete</button>
            <button @click="fetchEmployees" :disabled="loading || tableLoading">Refresh</button>
          </div>

        <table class="site-table">
          <thead>
            <tr>
              <th>Employee No</th>
              <th>Employee Name</th>
              <th>Mobile Number</th>
              <th>Department</th>
              <th>Site</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in employees" :key="item.emp_no"
                :class="{ 'selected': selectedEmployee && selectedEmployee.emp_no === item.emp_no }"
                @click="selectEmployee(item)">
                 <td>{{ item.emp_no }}</td>
                 <td>{{ item.emp_name }}</td>
                 <td>{{ item.mobile }}</td>
                 <td>{{ item.department }}</td>
                 <td>{{ employeeSiteName(item) }}</td>
              </tr>
               <tr v-if="employees.length === 0 && !tableLoading">
                 <td colspan="5" style="text-align: center;">No employees found.</td>
               </tr>
          </tbody>
        </table>
      </div>
    </div>

      <div v-if="notification.show" :class="['notification', notification.type, { 'fade-out': !notification.show }]">
      {{ notification.message }}.
      </div>

  </div>
</template>

<style scoped>
/* Styles based on previous views for consistency and professional look */
.employees-page { /* Unique class name for this page */
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f4f6f8;
    min-height: calc(100vh - 40px); /* Ensure page takes at least full height */
    color: #333; /* Default text color */
}

.container {
    display: flex;
    gap: 20px;
    max-width: 1200px; /* Adjusted max width for simplified table */
    margin: 0 auto;
    flex-wrap: wrap;
    align-items: flex-start; /* Align items to the top */
}

.form-panel, .table-panel {
    background-color: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    min-width: 300px;
     box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

.form-panel {
     flex: 1 1 350px; /* Flex-grow, Flex-shrink, Basis */
     max-width: 400px; /* Optional: Limit max width for form */
}

.table-panel {
     flex: 2 1 600px; /* Adjusted flex basis for simplified table */
    overflow-x: auto;
}


h2 {
    color: #333;
    margin-top: 0;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    font-size: 1.5rem;
    font-weight: 600; /* Slightly bolder heading */
}

.form-group {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    display: block;
    color: #555;
    font-weight: bold;
    font-size: 0.95rem;
}

input[type="text"],
input[type="number"],
select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 1rem;
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
    opacity: 0.8;
}

.form-actions {
    margin-top: 30px;
    display: flex;
    gap: 10px;
     flex-wrap: wrap; /* Allow buttons to wrap on small screens */
}

.save-button,
.cancel-button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     min-width: 90px; /* Ensure minimum button width */
     text-align: center;
     flex-grow: 1; /* Allow buttons to grow if needed */
}

.save-button {
    background-color: #28a745; /* Green */
    color: white;
}

.save-button:hover:not(:disabled) {
    background-color: #218838;
}

.cancel-button {
    background-color: #6c757d; /* Gray */
    color: white;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
}


.table-actions {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.table-actions button {
    padding: 8px 12px;
    border: 1px solid #007bff;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     flex-grow: 1;
     min-width: 60px;
     text-align: center;
}

/* Specific button styles */
.table-actions button:nth-child(2) { /* Edit button */
    background-color: #ffc107;
    border-color: #ffc107;
    color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
    background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
    background-color: #c82333;
}

.table-actions button:hover:not(:disabled) {
    background-color: #0056b3;
    border-color: #0056b3;
}


.site-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 0.95rem;
    border: 1px solid #ddd;
}

.site-table th,
.site-table td {
    padding: 12px;
    border: 1px solid #ddd;
    text-align: left;
    word-break: break-word;
}

.site-table th {
    background-color: #e9ecef;
    font-weight: bold;
    color: #495057;
    text-transform: uppercase;
    font-size: 0.85rem;
}

 .site-table tbody tr:nth-child(even) {
      background-color: #f8f9fa;
 }

.site-table tbody tr:hover {
    background-color: #e9ecef;
    cursor: pointer;
}

.site-table tbody tr.selected {
    background-color: #cfe2ff;
     outline: 2px solid #007bff;
     outline-offset: -2px;
}

 /* Center text in empty cell */
 .site-table td[colspan="5"] { /* Updated colspan */
      text-align: center;
      font-style: italic;
      color: #666;
 }


/* Responsive adjustments */
@media (max-width: 768px) {
    .container {
        flex-direction: column;
        gap: 15px;
    }

    .form-panel,
    .table-panel {
        margin-bottom: 15px;
        min-width: unset;
        flex: unset;
        width: 100%;
        padding: 15px;
    }

    h2 {
        margin-bottom: 15px;
        padding-bottom: 10px;
        font-size: 1.3rem;
    }

    .form-group {
        margin-bottom: 15px;
    }

    .form-actions {
        margin-top: 20px;
        flex-direction: column;
        gap: 8px;
    }

    .save-button, .cancel-button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }

    .table-actions {
        flex-direction: column;
        gap: 8px;
    }

    .table-actions button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }
}

/* Notification Styling */
.notification {
    position: fixed;
    bottom: 20px;
    left: 20px;
    padding: 15px 20px;
    border-radius: 4px;
    color: white;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
     opacity: 1;
     transition: opacity 0.5s ease-in-out;
}

 .notification.fade-out {
     opacity: 0;
 }


.notification.success {
    background-color: #28a745;
}

.notification.error {
    background-color: #dc3545;
}

.notification.warning {
    background-color: #ffc107;
    color: #212529;
}

.error-message {
    color: #dc3545;
    margin-bottom: 15px;
    padding: 10px;
    border: 1px solid #dc3545;
    background-color: #fff3f3;
    border-radius: 4px;
     font-size: 0.9rem;
}

</style>
